import * as Sentry from '@sentry/react';
import { type ChangeEvent, useRef } from 'react';
import { Text } from '@pasqal/core/ui/components';
import { useAlert } from '~/systems/alert';
import ExampleGallery from '../ExampleGallery/ExampleGallery';
import Icon from '../Icon/Icon';
import Card from '../ui/Card/Card';
import './NewExperimentTab.css';

interface NewExperimentTabProps {
  onCreateSequence: (fromSequence?: string) => void;
}

// Eagerly load example JSON files
const exampleModules = import.meta.glob('/src/models/examples/*.json', {
  eager: true
}) as Record<string, { default: unknown }>;

export default function NewExperimentTab({
  onCreateSequence
}: NewExperimentTabProps) {
  const alertSys = useAlert();
  const fileInput = useRef<HTMLInputElement>(null);

  async function handleLoadExample(exampleId: string) {
    try {
      const mod = exampleModules[`/src/models/examples/${exampleId}.json`];
      if (!mod) {
        throw new Error(`Example not found: ${exampleId}`);
      }
      onCreateSequence(JSON.stringify(mod.default));
    } catch (e) {
      Sentry.captureException(e);
      alertSys.setAlert((e as Error).message);
    }
  }

  function triggerImportFile() {
    fileInput.current?.click();
  }

  async function onImportFile(e: ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    if (input.files?.length === 1) {
      const file = input.files[0];
      const text = await file.text();
      onCreateSequence(text);
    }
  }

  return (
    <div className="NewExperimentTab">
      <div className="NewExperimentTab-content">
        <div className="NewExperimentTab-actionsRow">
          <Card
            className="NewExperimentTab-action NewExperimentTab-action--new"
            onClick={() => onCreateSequence()}
          >
            <Icon kind="plus" size={48} />
            <Text>New experiment</Text>
          </Card>
          <input ref={fileInput} type="file" hidden onChange={onImportFile} />
          <Card
            className="NewExperimentTab-action NewExperimentTab-action--import"
            onClick={triggerImportFile}
          >
            <Icon kind="import" size={48} />
            <Text>Import experiment</Text>
          </Card>
        </div>
        <div className="NewExperimentTab-galleryLabel">
          <Text font="medium">Or import from the example gallery</Text>
        </div>
        <ExampleGallery onLoadExample={handleLoadExample} />
      </div>
    </div>
  );
}
